import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { Staff } from '@/types/settings-api';

/**
 * Update a staff member (PROD-765).
 *   PUT /api/Staff/{staffSetupId}
 *
 * Flutter parity: `updateStaffById` in `settings_data_source_impl.dart:936`.
 * Body shape mirrors `useCreateStaff` — see notes there. The `staffSetupId`
 * appears both in the URL path AND in the body (Flutter sends both); don't
 * second-guess and strip it from the body.
 *
 * `specialities[]` + `assignStaffToSpecialities[]` are preserved verbatim
 * across saves in v1 (no UI to edit them yet); see staff-edit-dialog for
 * the merge invariant.
 */
export function useUpdateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staff: Staff) => {
      if (!staff.staffSetupId) {
        throw new Error('useUpdateStaff requires a non-null staffSetupId');
      }
      return put<unknown>(`/api/Staff/${staff.staffSetupId}`, staff);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'staff-list'] });
      queryClient.invalidateQueries({
        queryKey: ['settings', 'staff-by-id', vars.staffSetupId],
      });
    },
  });
}
